/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { backendClientsCrud } from '@services/housien';

export interface LeaseClientLabel {
  name: string;
  document: string;
  /** First phone-like channel (phone, WhatsApp, home, work), as stored. */
  phone: string;
  email: string;
}

const PHONE_TYPES = ['phone', 'whatsapp', 'phone_home', 'phone_work'];

/**
 * The first contact value of the given types.
 *
 * Reachable channels live as a typed list under `assignment.contacts`, not
 * as `phone`/`email` fields (see DealClientCard).
 *
 * @param client - The raw client record.
 * @param types - The contact types to accept, in order of preference.
 * @returns The value, or an empty string.
 */
function contactOf(client: Record<string, any>, types: string[]): string {
  const contacts: Array<{ type?: string; value?: string }> = client.assignment?.contacts ?? [];
  for (const type of types) {
    const value = contacts.find((c) => c.type === type)?.value;
    if (value) return value;
  }
  return '';
}

/** Client ids resolved to display identity; missing ids are simply absent. */
export type LeaseClientLabels = Record<string, LeaseClientLabel>;

/**
 * Display name of a client record, across the flat and nested shapes.
 */
function clientDisplayName(client: Record<string, any>): string {
  return (
    client.personal_info?.full_name
    ?? client.full_name
    ?? client.identity?.corporate_name
    ?? client.corporate_name
    ?? ''
  );
}

/**
 * CPF or CNPJ off the client's identity, whichever the person type carries.
 */
function clientDocument(client: Record<string, any>): string {
  return client.identity?.cpf ?? client.identity?.cnpj ?? '';
}

/**
 * Resolves the clients a set of leases reference (tenant, landlords) into
 * display name/document -- never stored on the lease itself [FK, not
 * snapshot]. Mirrors `useLeasePropertyLabels`: fetched one id at a time so
 * repeated clients across leases cost one cached request each, with no
 * silent cap from a paged search endpoint.
 *
 * @param agencyId - Agency owning the clients.
 * @param clientIds - Ids to resolve; duplicates and blanks are ignored.
 * @returns A map of id to {name, document}, plus whether any lookup is
 *   still running.
 */
export function useLeaseClientLabels(agencyId: string, clientIds: string[]) {
  const uniqueIds = useMemo(
    () => [...new Set(clientIds.filter(Boolean))].sort(),
    [clientIds],
  );

  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['leases', agencyId, 'client-label', id] as const,
      queryFn: () => backendClientsCrud(agencyId).getById(id),
      enabled: Boolean(agencyId && id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const labels = useMemo<LeaseClientLabels>(() => {
    const map: LeaseClientLabels = {};
    results.forEach((result, index) => {
      if (result.data) {
        map[uniqueIds[index]] = {
          name: clientDisplayName(result.data as Record<string, any>),
          document: clientDocument(result.data as Record<string, any>),
          phone: contactOf(result.data as Record<string, any>, PHONE_TYPES),
          email: contactOf(result.data as Record<string, any>, ['email']),
        };
      }
    });
    return map;
  }, [results, uniqueIds]);

  return { labels, isLoading: results.some((r) => r.isLoading) };
}
